import { useState, useEffect, useRef, useCallback } from 'react';
import {
  useVendorCatalogShopService,
  useEventProductStreaming,
} from '@actschurch/ccms-sdk';
import type { ShopEventProductModelView } from '@actschurch/ccms-sdk';
import {
  Container,
  Stack,
  Group,
  Card,
  Title,
  Text,
  Button,
  TextInput,
  Badge,
  Progress,
  Alert,
  SimpleGrid,
  Divider,
  Code,
  Table,
  Image,
} from '@/components/ui';

export default function StreamingTestPage() {
  const [eventId, setEventId] = useState('');
  const [activeEventId, setActiveEventId] = useState<number | null>(null);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Title order={2}>Event Product Streaming Test</Title>
        <Text c="dimmed">
          Enter an Event ID to stream all products for that event using the SDK.
          Products load progressively in batches.
        </Text>

        <Group>
          <TextInput
            placeholder="Enter Event ID"
            value={eventId}
            onChange={(e) => setEventId(e.currentTarget.value)}
            style={{ width: 200 }}
          />
          <Button
            onClick={() => {
              const id = parseInt(eventId, 10);
              if (!isNaN(id) && id > 0) {
                setActiveEventId(id);
              }
            }}
            disabled={!eventId || isNaN(parseInt(eventId, 10))}
          >
            Stream Products
          </Button>
          {activeEventId && (
            <Button variant="outline" onClick={() => setActiveEventId(null)}>
              Clear
            </Button>
          )}
        </Group>

        <Divider />

        {activeEventId ? (
          <StreamingResults eventId={activeEventId} />
        ) : (
          <RawFetchTest />
        )}
      </Stack>
    </Container>
  );
}

/**
 * Streaming results — uses useEventProductStreaming hook
 */
function StreamingResults({ eventId }: { eventId: number }) {
  const {
    products,
    isLoading,
    isDone,
    progress,
    loadedCount,
    totalCount,
    error,
    load,
    reset,
  } = useEventProductStreaming(eventId, 10);

  const [batches, setBatches] = useState<number[]>([]);
  const prevCount = useRef(0);

  useEffect(() => {
    reset();
    setBatches([]);
    prevCount.current = 0;
    load();
  }, [eventId]);

  // Track batch arrivals
  useEffect(() => {
    if (loadedCount > prevCount.current) {
      const batchSize = loadedCount - prevCount.current;
      setBatches((prev) => [...prev, batchSize]);
      prevCount.current = loadedCount;
    }
  }, [loadedCount]);

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={4}>Event ID: {eventId}</Title>
            <Group gap="xs">
              {isLoading && <Badge color="blue">Loading</Badge>}
              {isDone && <Badge color="green">Done</Badge>}
              {error && <Badge color="red">Error</Badge>}
            </Group>
          </Group>

          <Progress value={progress} size="lg" striped animated={isLoading} />

          <Group gap="lg">
            <Text size="sm">
              <strong>{loadedCount}</strong> / <strong>{totalCount}</strong> products loaded
            </Text>
            <Text size="sm" c="dimmed">
              Progress: {progress}%
            </Text>
            <Text size="sm" c="dimmed">
              Batches received: {batches.length} ({batches.join(', ')})
            </Text>
          </Group>

          {error && (
            <Alert color="red" title="Streaming Error">
              {error.message}
            </Alert>
          )}

          <Group>
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                reset();
                setBatches([]);
                prevCount.current = 0;
                load();
              }}
            >
              Reload
            </Button>
          </Group>
        </Stack>
      </Card>

      {isDone && products.length === 0 && (
        <Alert color="yellow" title="No Products">
          No products found for this event. Check that the event exists and has confirmed vendors with active products.
        </Alert>
      )}

      {products.length > 0 && (
        <>
          <Title order={4}>Products ({products.length})</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
            {products.map((product) => (
              <ProductCard key={product.productID} product={product} />
            ))}
          </SimpleGrid>

          <Divider my="md" />
          <Title order={4}>Raw Data (first 3 products)</Title>
          <Code block>{JSON.stringify(products.slice(0, 3), null, 2)}</Code>
        </>
      )}
    </Stack>
  );
}

/**
 * Product card display
 */
function ProductCard({ product }: { product: ShopEventProductModelView }) {
  return (
    <Card withBorder>
      <Stack gap="xs">
        {product.primaryMediaUrl && (
          <Image
            src={product.primaryMediaUrl}
            style={{ height: 120 }}
            fit="cover"
            radius="sm"
          />
        )}
        <Text fw={600} lineClamp={1}>{product.name}</Text>
        <Group gap="xs">
          <Badge size="sm" variant="light">{product.priceDisplay}</Badge>
          {product.isFeatured && <Badge size="sm" color="yellow">Featured</Badge>}
          {product.availabilityBadge && (
            <Badge size="sm" variant="outline">{product.availabilityBadge}</Badge>
          )}
          {product.isSoldOut && <Badge size="sm" color="red">Sold Out</Badge>}
          {product.isLowStock && !product.isSoldOut && (
            <Badge size="sm" color="orange">{product.stockBadge}</Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          Vendor: {product.vendorName ?? `ID ${product.vendorID}`}
        </Text>
        <Text size="xs" c="dimmed">
          Product ID: {product.productID} | Event ID: {product.eventID}
        </Text>
        {product.productVariants && product.productVariants.length > 0 && (
          <Text size="xs" c="dimmed">
            Variants: {product.productVariants.length}
          </Text>
        )}
        {product.productAddOns && product.productAddOns.length > 0 && (
          <Text size="xs" c="dimmed">
            Add-ons: {product.productAddOns.length}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Raw single-page fetch test (shown when no eventId is active)
 */
function RawFetchTest() {
  const shopService = useVendorCatalogShopService();
  const [rawEventId, setRawEventId] = useState('');
  const [rawResult, setRawResult] = useState<string | null>(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState<string | null>(null);

  const fetchSinglePage = useCallback(async () => {
    const id = parseInt(rawEventId, 10);
    if (isNaN(id) || id <= 0) return;

    setRawLoading(true);
    setRawError(null);
    setRawResult(null);

    try {
      const response = await shopService.getEventProductsRaw(id, 0, 5);
      setRawResult(JSON.stringify(response, null, 2));
    } catch (err) {
      setRawError(err instanceof Error ? err.message : String(err));
    } finally {
      setRawLoading(false);
    }
  }, [rawEventId, shopService]);

  return (
    <Stack gap="md">
      <Title order={4}>Raw Single-Page Fetch (Debug)</Title>
      <Text size="sm" c="dimmed">
        Fetches the first 5 products for an event using getEventProductsRaw(eventId, 0, 5).
        Useful to verify the API endpoint works before testing the full stream.
      </Text>
      <Group>
        <TextInput
          placeholder="Event ID"
          value={rawEventId}
          onChange={(e) => setRawEventId(e.currentTarget.value)}
          style={{ width: 200 }}
        />
        <Button
          variant="outline"
          onClick={fetchSinglePage}
          loading={rawLoading}
          disabled={!rawEventId || isNaN(parseInt(rawEventId, 10))}
        >
          Fetch Single Page
        </Button>
      </Group>

      {rawError && (
        <Alert color="red" title="Fetch Error">
          {rawError}
        </Alert>
      )}

      {rawResult && (
        <Code block style={{ maxHeight: 500, overflow: 'auto' }}>
          {rawResult}
        </Code>
      )}
    </Stack>
  );
}
